import { useEffect, useState } from "react";
import { Search, Mail, Loader2, Sparkles, Download, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const AdminEarlyAccess = () => {
  const { api } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const limit = 50;

  const load = async (nextSkip = 0, query = q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/early-access", {
        params: { skip: nextSkip, limit, q: query || undefined },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSkip(nextSkip);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load signups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(0, ""); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => {
    e.preventDefault();
    load(0, q.trim());
  };

  const copyAllEmails = async () => {
    const emails = items.map((i) => i.email).join(", ");
    try {
      await navigator.clipboard.writeText(emails);
      toast.success(`Copied ${items.length} emails to clipboard`);
    } catch { toast.error("Clipboard blocked. Select manually."); }
  };

  const downloadCsv = () => {
    const header = ["email", "name", "role", "category_interest", "source", "created_at"];
    const rows = items.map((i) => header.map((k) => JSON.stringify(i[k] ?? "")).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dwinov-early-access-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const canPrev = skip > 0;
  const canNext = skip + limit < total;

  return (
    <div data-testid="admin-early-access-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-saffron-700">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Early Access</span>
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-navy-800">Signup Waitlist</h1>
          <p className="mt-1 text-sm text-navy-500">
            {loading ? "Loading…" : `${total.toLocaleString()} people have registered for early access.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyAllEmails}
            disabled={!items.length}
            data-testid="admin-ea-copy-emails"
            className="btn-navy-outline border-2 inline-flex items-center gap-1.5 rounded-2xl h-10 px-4 text-sm font-semibold disabled:opacity-50"
          >
            <Copy className="h-4 w-4" /> Copy emails
          </button>
          <button
            onClick={downloadCsv}
            disabled={!items.length}
            data-testid="admin-ea-download-csv"
            className="btn-saffron inline-flex items-center gap-1.5 rounded-2xl h-10 px-4 text-sm font-semibold disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </div>

      <form onSubmit={onSearch} className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy-400" />
          <Input
            data-testid="admin-ea-search"
            placeholder="Search email or name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-2xl border-warm-200 focus-visible:ring-saffron-500/20"
          />
        </div>
        <button
          type="submit"
          className="btn-navy rounded-2xl h-10 px-4 text-sm font-semibold"
          data-testid="admin-ea-search-submit"
        >
          Search
        </button>
      </form>

      <div className="rounded-2xl border border-warm-200 bg-white overflow-hidden shadow-warm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream-100 text-navy-500">
              <tr>
                <th className="text-left font-semibold px-4 py-3">Email</th>
                <th className="text-left font-semibold px-4 py-3">Name</th>
                <th className="text-left font-semibold px-4 py-3">Role</th>
                <th className="text-left font-semibold px-4 py-3">Category interest</th>
                <th className="text-left font-semibold px-4 py-3">Source</th>
                <th className="text-left font-semibold px-4 py-3">Registered</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-navy-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-16 text-center text-navy-500">
                  <Mail className="h-8 w-8 mx-auto mb-3 text-navy-300" />
                  No early-access signups yet.
                </td></tr>
              ) : items.map((it) => (
                <tr key={it.id} data-testid={`admin-ea-row-${it.id}`} className="border-t border-warm-100 hover:bg-cream-100/60">
                  <td className="px-4 py-3 font-medium text-navy-800">{it.email}</td>
                  <td className="px-4 py-3 text-navy-700">{it.name || <span className="text-navy-400">—</span>}</td>
                  <td className="px-4 py-3">
                    {it.role ? (
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                        it.role === "creator" ? "bg-saffron-500/15 text-saffron-700" : "bg-forest-500/15 text-forest-700"
                      }`}>{it.role}</span>
                    ) : <span className="text-navy-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-navy-700">{it.category_interest || <span className="text-navy-400">—</span>}</td>
                  <td className="px-4 py-3 text-navy-500">{it.source || <span className="text-navy-400">—</span>}</td>
                  <td className="px-4 py-3 text-navy-500 whitespace-nowrap">{fmtDate(it.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(canPrev || canNext) && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-warm-100 bg-cream-100/60">
            <span className="text-xs text-navy-500">
              Showing {items.length ? skip + 1 : 0}–{skip + items.length} of {total}
            </span>
            <div className="flex gap-2">
              <button
                disabled={!canPrev}
                onClick={() => load(Math.max(skip - limit, 0), q)}
                className="rounded-xl border border-warm-200 h-8 px-3 text-xs font-semibold text-navy-700 hover:bg-warm-100 disabled:opacity-40"
              >Previous</button>
              <button
                disabled={!canNext}
                onClick={() => load(skip + limit, q)}
                className="rounded-xl border border-warm-200 h-8 px-3 text-xs font-semibold text-navy-700 hover:bg-warm-100 disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEarlyAccess;
